import { Injectable } from "@angular/core";

import { Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";

import { EBrugerInfo } from "@e-forsyning/common/bruger";
import { InstallationHttpClient, ParametreForAktuelleInstallation, UmbracoHttpClient } from "@e-forsyning/common/http";
import { UmbracoIndstillingerObserver } from "@e-forsyning/common/umbraco-indstillinger";

import { ForbrugsnotifikationIndstillingerDto } from "../model/forbrugsnotifikation-indstillinger.dto";
import {
    ForbrugsnotifikationIndstillinger,
    ForbrugsnotifikationSektionIndstilling,
    ForbrugsnotifikationUdsendelseIndstilling
} from "../model/forbrugsnotifikation-indstillinger.model";
import {
    ForbrugsnotifikationFrekvens,
    ForbrugsnotifikationOpsaetning,
    ForbrugsnotifikationType
} from "../model/forbrugsnotifikation-opsaetning.model";

@Injectable({ providedIn: "root" })
export class ForbrugsnotifikationRepository {
    constructor(
        private readonly umbracoHttpClient: UmbracoHttpClient,
        private readonly installationHttpClient: InstallationHttpClient,
        private readonly indstillinger: UmbracoIndstillingerObserver
    ) {}

    public hentForbrugsnotifikationOpsaetning(): Observable<ForbrugsnotifikationOpsaetning> {
        return this.installationHttpClient
            .get<EBrugerInfo>("api/getebrugerinfo", ParametreForAktuelleInstallation.true)
            .pipe(map((result) => this.mapBrugerToData(result)));
    }

    public gemForbrugsnotifikationOpsaetning(opsaetning: ForbrugsnotifikationOpsaetning): Promise<void> {
        const result = this.installationHttpClient
            .get<EBrugerInfo>("api/getebrugerinfo", ParametreForAktuelleInstallation.true)
            .pipe(
                map((ebruger) => ({ ...ebruger, ...this.mapOpsaetningToDto(opsaetning) })),
                switchMap((ebruger) =>
                    this.installationHttpClient.postWithStringResult(
                        "api/updateebrugernotifikation",
                        ebruger,
                        ParametreForAktuelleInstallation.true
                    )
                ),
                map(() => null)
            )
            .toPromise();
        return result;
    }

    public hentIndstillinger(): Observable<ForbrugsnotifikationIndstillinger> {
        return this.indstillinger.valueChanges.pipe(
            switchMap((indstillinger) =>
                this.umbracoHttpClient.get<ForbrugsnotifikationIndstillingerDto>(
                    `GetForbrugsoverblikSettings?id=${indstillinger.aktuelEforsyningNodeId}`
                )
            ),
            map((i) => this.mapIndstillingerToModel(i))
        );
    }

    private mapIndstillingerToModel(dto: ForbrugsnotifikationIndstillingerDto): ForbrugsnotifikationIndstillinger {
        const forbrug = this.mapSektionToModel(
            dto.ForbrugOverskrift,
            dto.ForbrugUndertekst,
            dto.VisForbrug,
            dto.ForbrugIndividuelValg,
            dto.ForbrugIndividuelTekst,
            dto.ForbrugStandardvalg,
            dto.ForbrugStandardTekst
        );

        const retur = this.mapSektionToModel(
            dto.ReturOverskrift,
            dto.ReturUndertekst,
            dto.VisReturtemperatur || dto.VisAfkoeling,
            dto.ReturIndividuelValg,
            dto.ReturIndividuelTekst,
            dto.ReturStandardValg,
            dto.ReturStandardTekst
        );

        const udsendelse = this.mapUdsendelseToModel(dto.Dag, dto.Uge, dto.Maaned, dto.SmsBesked, dto.MailBesked);

        return {
            menuTekst: dto.MenuTekst,
            titel: dto.OverskriftBesked,
            beskrivelse: dto.BeskrivelseBesked,
            forbrug,
            retur,
            udsendelse
        };
    }

    private mapSektionToModel(
        titel: string,
        subtitel: string,
        synlig: boolean,
        brugerdefineretSynlig: boolean,
        brugerdefineretTekst: string,
        standardSynlig: boolean,
        standardTekst: string
    ): ForbrugsnotifikationSektionIndstilling {
        return {
            titel,
            subtitel,
            synlig,
            brugerdefineret: {
                synlig: brugerdefineretSynlig,
                tekst: brugerdefineretTekst
            },
            standard: {
                synlig: standardSynlig,
                tekst: standardTekst
            }
        };
    }

    private mapUdsendelseToModel(
        udsendDagligt: boolean,
        udsendUgentligt: boolean,
        udsendMaanedligt: boolean,
        typeSms: boolean,
        typeMail: boolean
    ): ForbrugsnotifikationUdsendelseIndstilling {
        return {
            intervaller: {
                daglig: udsendDagligt,
                ugentlig: udsendUgentligt,
                maanedlig: udsendMaanedligt
            },
            typer: {
                sms: typeSms,
                mail: typeMail
            }
        };
    }

    private mapBrugerToData(dto: EBrugerInfo): ForbrugsnotifikationOpsaetning {
        return {
            forbrug: {
                brugerdefineret: !!+dto.overvaagningstypeforbrug,
                vaerdi: dto.vaerdiforbrug
            },
            retur: {
                brugerdefineret: !!+dto.overvaagningstypeinstallation,
                vaerdi: dto.vaerdiinstallation
            },
            udsendelse: {
                mail: dto.notifikationmail,
                mobil: dto.notifikationmobil,
                frekvens: this.mapFrekvensToModel(dto.frekvens),
                type: this.mapTypeToModel(dto.notifikationtype)
            }
        };
    }

    private mapFrekvensToModel(value: number): ForbrugsnotifikationFrekvens {
        switch (value) {
            case 10:
                return ForbrugsnotifikationFrekvens.daglig;
            case 20:
                return ForbrugsnotifikationFrekvens.ugentlig;
            case 30:
            default:
                return ForbrugsnotifikationFrekvens.maanedlig;
        }
    }

    private mapFrekvensToDto(frekvens: ForbrugsnotifikationFrekvens): number {
        switch (frekvens) {
            case ForbrugsnotifikationFrekvens.daglig:
                return 10;
            case ForbrugsnotifikationFrekvens.ugentlig:
                return 20;
            case ForbrugsnotifikationFrekvens.maanedlig:
            default:
                return 30;
        }
    }

    private mapTypeToModel(value: string): ForbrugsnotifikationType {
        switch (value) {
            case "email":
                return ForbrugsnotifikationType.mail;
            case "sms":
                return ForbrugsnotifikationType.sms;
            case "":
            default:
                return ForbrugsnotifikationType.brev;
        }
    }

    private mapTypeToDto(type: ForbrugsnotifikationType): string {
        switch (type) {
            case ForbrugsnotifikationType.mail:
                return "email";
            case ForbrugsnotifikationType.sms:
                return "sms";
            case ForbrugsnotifikationType.brev:
            default:
                return "";
        }
    }

    private mapOpsaetningToDto(opsaetning: ForbrugsnotifikationOpsaetning): Partial<EBrugerInfo> {
        const result: Partial<EBrugerInfo> = {
            frekvens: this.mapFrekvensToDto(opsaetning.udsendelse.frekvens),
            notifikationmail: opsaetning.udsendelse.mail,
            notifikationmobil: opsaetning.udsendelse.mobil,
            notifikationtype: this.mapTypeToDto(opsaetning.udsendelse.type),
            overvaagningstypeforbrug: this.mapBrugerdefineretToDto(opsaetning.forbrug.brugerdefineret),
            overvaagningstypeinstallation: this.mapBrugerdefineretToDto(opsaetning.retur.brugerdefineret),
            vaerdiforbrug: opsaetning.forbrug.vaerdi,
            vaerdiinstallation: opsaetning.retur.vaerdi
        };
        return result;
    }

    private mapBrugerdefineretToDto(brugerdefineret: boolean): string {
        return brugerdefineret ? "10" : "0";
    }
}
